import { useState, useEffect, lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { getListings, photoUrl } from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Search, SlidersHorizontal, X, LayoutGrid, Map, MapPin, Locate } from "lucide-react";

const ListingMap = lazy(() => import("../components/ListingMap"));

// Virginia city coordinates for quick search
const CITY_COORDS = {
  // Virginia — Hampton Roads
  "Norfolk": { lat: 36.8508, lng: -76.2859 },
  "Virginia Beach": { lat: 36.8529, lng: -75.9780 },
  "Suffolk": { lat: 36.7282, lng: -76.5836 },
  "Chesapeake": { lat: 36.7682, lng: -76.2875 },
  "Newport News": { lat: 37.0871, lng: -76.4730 },
  "Hampton": { lat: 37.0299, lng: -76.3452 },
  "Portsmouth": { lat: 36.8354, lng: -76.2983 },
  "Williamsburg": { lat: 37.2707, lng: -76.7075 },
  "Yorktown": { lat: 37.2387, lng: -76.5097 },
  // Virginia — Northern VA / NoVA
  "Arlington": { lat: 38.8799, lng: -77.1068 },
  "Alexandria": { lat: 38.8048, lng: -77.0469 },
  "Falls Church": { lat: 38.8823, lng: -77.1712 },
  "Springfield": { lat: 38.7893, lng: -77.1872 },
  "West Springfield": { lat: 38.7734, lng: -77.2258 },
  "Fairfax": { lat: 38.8462, lng: -77.3064 },
  "McLean": { lat: 38.9339, lng: -77.1773 },
  "Reston": { lat: 38.9687, lng: -77.3411 },
  "Herndon": { lat: 38.9696, lng: -77.3861 },
  "Vienna": { lat: 38.9012, lng: -77.2653 },
  "Tysons": { lat: 38.9187, lng: -77.2311 },
  "Centreville": { lat: 38.8404, lng: -77.4289 },
  "Chantilly": { lat: 38.8943, lng: -77.4311 },
  "Manassas": { lat: 38.7509, lng: -77.4753 },
  "Woodbridge": { lat: 38.6582, lng: -77.2497 },
  "Dumfries": { lat: 38.5674, lng: -77.3275 },
  "Dale City": { lat: 38.6332, lng: -77.3114 },
  "Quantico": { lat: 38.5221, lng: -77.2943 },
  "Fort Belvoir": { lat: 38.7119, lng: -77.1453 },
  "Stafford": { lat: 38.4221, lng: -77.4083 },
  "Fredericksburg": { lat: 38.3032, lng: -77.4606 },
  "Spotsylvania": { lat: 38.2026, lng: -77.5950 },
  "Annandale": { lat: 38.8304, lng: -77.1961 },
  "Burke": { lat: 38.7901, lng: -77.2717 },
  "Lorton": { lat: 38.7043, lng: -77.2278 },
  "Mount Vernon": { lat: 38.7295, lng: -77.1075 },
  "Great Falls": { lat: 38.9995, lng: -77.2875 },
  "Oakton": { lat: 38.8898, lng: -77.3003 },
  "Merrifield": { lat: 38.8712, lng: -77.2297 },
  "Gainesville": { lat: 38.7959, lng: -77.6142 },
  "Haymarket": { lat: 38.8123, lng: -77.6369 },
  "Bristow": { lat: 38.7321, lng: -77.5394 },
  "Ashburn": { lat: 39.0437, lng: -77.4875 },
  "Leesburg": { lat: 39.1157, lng: -77.5636 },
  "Sterling": { lat: 39.0065, lng: -77.4286 },
  "Purcellville": { lat: 39.1362, lng: -77.7147 },
  "Warrenton": { lat: 38.7135, lng: -77.7953 },
  "Culpeper": { lat: 38.4729, lng: -77.9964 },
  "Front Royal": { lat: 38.9182, lng: -78.1944 },
  "Winchester": { lat: 39.1857, lng: -78.1633 },
  // DC
  "Washington DC": { lat: 38.9072, lng: -77.0369 },
  // Maryland
  "Bethesda": { lat: 38.9847, lng: -77.0947 },
  "Silver Spring": { lat: 38.9907, lng: -77.0261 },
  "Rockville": { lat: 39.0840, lng: -77.1528 },
  "Gaithersburg": { lat: 39.1434, lng: -77.2014 },
  "Germantown": { lat: 39.1732, lng: -77.2717 },
  "College Park": { lat: 38.9807, lng: -76.9370 },
  "Bowie": { lat: 38.9430, lng: -76.7302 },
  "Largo": { lat: 38.8976, lng: -76.8305 },
  "Laurel": { lat: 39.0993, lng: -76.8483 },
  "Hyattsville": { lat: 38.9557, lng: -76.9453 },
  "Greenbelt": { lat: 38.9957, lng: -76.8827 },
  "Takoma Park": { lat: 38.9779, lng: -77.0075 },
  "Frederick": { lat: 39.4143, lng: -77.4105 },
  "Waldorf": { lat: 38.6246, lng: -76.9391 },
  "Capitol Heights": { lat: 38.8851, lng: -76.9158 },
  "New Carrollton": { lat: 38.9698, lng: -76.8719 },
};

export default function BrowseListings() {
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // grid | map | split
  const [searchText, setSearchText] = useState(searchParams.get("search") || "");
  const [filters, setFilters] = useState({
    property_type: searchParams.get("property_type") || "",
    lease_type: searchParams.get("lease_type") || "",
    city: searchParams.get("city") || "",
    min_price: 0,
    max_price: 3000,
    bah_eligible: searchParams.get("bah_eligible") === "true" ? true : null,
    pet_friendly: null,
  });
  const [mapCenter, setMapCenter] = useState(null);
  const [searchRadius, setSearchRadius] = useState(15); // miles

  const fetchListings = async (geoSearch = false) => {
    setLoading(true);
    const params = {};
    if (searchText) params.search = searchText;
    if (filters.property_type) params.property_type = filters.property_type;
    if (filters.lease_type) params.lease_type = filters.lease_type;
    if (filters.city && !geoSearch) params.city = filters.city;
    if (filters.min_price > 0) params.min_price = filters.min_price;
    if (filters.max_price < 3000) params.max_price = filters.max_price;
    if (filters.bah_eligible) params.bah_eligible = true;
    if (filters.pet_friendly) params.pet_friendly = true;
    if (geoSearch && mapCenter) {
      params.lat = mapCenter[0];
      params.lng = mapCenter[1];
      params.radius = searchRadius;
    }
    try {
      const res = await getListings(params);
      setListings(res.data.listings);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchListings();
  }, []);

  // When city changes, set map center
  useEffect(() => {
    if (filters.city && CITY_COORDS[filters.city]) {
      setMapCenter([CITY_COORDS[filters.city].lat, CITY_COORDS[filters.city].lng]);
    }
  }, [filters.city]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchListings(!!mapCenter);
  };

  const handleSearchNearby = () => {
    if (mapCenter) {
      fetchListings(true);
    }
  };

  const handleMyLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const center = [pos.coords.latitude, pos.coords.longitude];
          setMapCenter(center);
          setViewMode("split");
        },
        () => {
          // Default to Northern Virginia center
          setMapCenter([38.82, -77.15]);
          setViewMode("split");
        }
      );
    } else {
      setMapCenter([38.82, -77.15]);
      setViewMode("split");
    }
  };

  const handleCityMapSearch = (city) => {
    const coords = CITY_COORDS[city];
    if (coords) {
      setMapCenter([coords.lat, coords.lng]);
      setViewMode("split");
      setFilters(f => ({ ...f, city }));
    }
  };

  const clearFilters = () => {
    setFilters({ property_type: "", lease_type: "", city: "", min_price: 0, max_price: 3000, bah_eligible: null, pet_friendly: null });
    setSearchText("");
    setMapCenter(null);
    fetchListings(false);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#faf8f3" }}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 md:px-12 py-6">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex gap-2 sm:gap-3 mb-4" data-testid="search-form">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888]" />
            <Input
              data-testid="search-input"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by location, title..."
              className="pl-10 h-11 rounded-xl bg-white border-black/[0.08]"
            />
          </div>
          <Button type="submit" data-testid="search-btn" className="h-11 px-5 rounded-xl bg-[#1a6b3c] hover:bg-[#15562f] text-white text-sm">
            Search
          </Button>
          <Button type="button" variant="outline" onClick={() => setShowFilters(!showFilters)} className="h-11 px-3 rounded-xl border-black/[0.08]" data-testid="filter-toggle-btn">
            <SlidersHorizontal className="w-4 h-4" />
          </Button>
        </form>

        {/* View Mode Toggle + Map Actions */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-1.5">
            <div className="flex bg-white rounded-lg border border-black/[0.08] p-0.5">
              {[
                { mode: "grid", Icon: LayoutGrid, label: "Grid" },
                { mode: "split", Icon: Map, label: "Map" },
              ].map(({ mode, Icon, label }) => (
                <button
                  key={mode}
                  data-testid={`view-${mode}-btn`}
                  onClick={() => {
                    setViewMode(mode);
                    if (mode === "split" && !mapCenter) setMapCenter([38.0, -77.0]);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    viewMode === mode ? "bg-[#1a6b3c] text-white" : "text-[#888] hover:text-[#0d0d0d]"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>
            {viewMode === "split" && (
              <div className="flex items-center gap-1.5 ml-2">
                <Button type="button" size="sm" variant="outline" onClick={handleMyLocation} className="h-8 rounded-lg border-black/[0.08] text-xs gap-1" data-testid="my-location-btn">
                  <Locate className="w-3.5 h-3.5" /> My Location
                </Button>
                <div className="flex items-center gap-1.5 bg-white rounded-lg border border-black/[0.08] px-2.5 py-1">
                  <span className="text-[10px] text-[#888] font-medium whitespace-nowrap">{searchRadius}mi</span>
                  <input
                    type="range" min="1" max="50" value={searchRadius}
                    onChange={(e) => setSearchRadius(parseInt(e.target.value))}
                    className="w-20 h-1 accent-[#1a6b3c]"
                    data-testid="radius-slider"
                  />
                </div>
                {mapCenter && (
                  <Button type="button" size="sm" onClick={handleSearchNearby} className="h-8 rounded-lg bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] text-xs font-semibold gap-1" data-testid="search-nearby-btn">
                    <MapPin className="w-3.5 h-3.5" /> Search Area
                  </Button>
                )}
              </div>
            )}
          </div>
          <p className="text-xs text-[#888]" data-testid="results-count">{total} {total === 1 ? "listing" : "listings"}</p>
        </div>

        {/* Quick City Pills (visible in map mode) */}
        {viewMode === "split" && (
          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="city-pills">
            {Object.keys(CITY_COORDS).map(city => (
              <button
                key={city}
                onClick={() => handleCityMapSearch(city)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  filters.city === city
                    ? "bg-[#1a6b3c] text-white border-[#1a6b3c]"
                    : "bg-white text-[#0d0d0d] border-black/[0.08] hover:border-[#1a6b3c]"
                }`}
                data-testid={`city-pill-${city.replace(/\s/g, "-").toLowerCase()}`}
              >
                {city}
              </button>
            ))}
          </div>
        )}

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white rounded-2xl p-5 border border-black/[0.06] mb-4 shadow-sm" data-testid="filters-panel">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">Property Type</label>
                <Select value={filters.property_type || undefined} onValueChange={(v) => setFilters(f => ({ ...f, property_type: v }))}>
                  <SelectTrigger data-testid="filter-property-type" className="rounded-xl border-black/[0.08]"><SelectValue placeholder="All Types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="room">Room</SelectItem>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="house">House</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">Lease Length</label>
                <Select value={filters.lease_type || undefined} onValueChange={(v) => setFilters(f => ({ ...f, lease_type: v }))}>
                  <SelectTrigger data-testid="filter-lease-type" className="rounded-xl border-black/[0.08]"><SelectValue placeholder="All Lengths" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short">Short-Term</SelectItem>
                    <SelectItem value="mid">Mid-Term</SelectItem>
                    <SelectItem value="long">Long-Term</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">City</label>
                <Select value={filters.city || undefined} onValueChange={(v) => setFilters(f => ({ ...f, city: v }))}>
                  <SelectTrigger data-testid="filter-city" className="rounded-xl border-black/[0.08]"><SelectValue placeholder="All Cities" /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(CITY_COORDS).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#0d0d0d] mb-1 block">Price: ${filters.min_price} – ${filters.max_price}</label>
                <Slider data-testid="filter-price-slider" min={0} max={3000} step={50}
                  value={[filters.min_price, filters.max_price]}
                  onValueChange={([min, max]) => setFilters(f => ({ ...f, min_price: min, max_price: max }))}
                  className="mt-3" />
              </div>
            </div>
            <div className="flex items-center gap-4 mt-4 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!filters.bah_eligible} onChange={(e) => setFilters(f => ({ ...f, bah_eligible: e.target.checked || null }))} className="rounded border-black/[0.08]" data-testid="filter-bah" />
                <span className="text-xs">BAH Eligible</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!filters.pet_friendly} onChange={(e) => setFilters(f => ({ ...f, pet_friendly: e.target.checked || null }))} className="rounded border-black/[0.08]" data-testid="filter-pets" />
                <span className="text-xs">Pet Friendly</span>
              </label>
              <Button onClick={() => fetchListings(!!mapCenter)} data-testid="apply-filters-btn" size="sm" className="ml-auto rounded-full bg-[#1a6b3c] hover:bg-[#15562f] text-white px-5 text-xs">
                Apply
              </Button>
              <Button onClick={clearFilters} variant="ghost" size="sm" className="text-[#888] text-xs" data-testid="clear-filters-btn">
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            </div>
          </div>
        )}

        {/* Content */}
        {viewMode === "grid" ? (
          // Grid View
          loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-black/[0.06] animate-pulse">
                  <div className="aspect-[4/3] bg-[#e5e3de]" />
                  <div className="p-5 space-y-3">
                    <div className="h-5 bg-[#e5e3de] rounded w-3/4" />
                    <div className="h-4 bg-[#e5e3de] rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-20">
              <Search className="w-12 h-12 text-[#d5d3ce] mx-auto mb-4" />
              <h3 className="text-xl font-black text-[#0d0d0d] mb-2 font-['Fraunces']">No listings found</h3>
              <p className="text-[#888] text-sm">Try adjusting your filters or search terms.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="listings-grid">
              {listings.map(listing => (
                <PropertyCard key={listing.listing_id} listing={listing} />
              ))}
            </div>
          )
        ) : (
          // Split Map + List View
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" style={{ minHeight: "calc(100vh - 240px)" }}>
            <div className="h-[500px] lg:h-auto lg:sticky lg:top-24">
              <Suspense fallback={
                <div className="w-full h-full rounded-2xl bg-[#e5e3de] animate-pulse flex items-center justify-center">
                  <Map className="w-8 h-8 text-[#888]" />
                </div>
              }>
                <ListingMap
                  listings={listings}
                  center={mapCenter}
                  radius={mapCenter ? searchRadius : null}
                />
              </Suspense>
            </div>
            <div className="space-y-3 overflow-y-auto" data-testid="map-listings-list" style={{ maxHeight: "calc(100vh - 240px)" }}>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-black/[0.06] animate-pulse flex gap-4">
                    <div className="w-28 h-20 rounded-lg bg-[#e5e3de] flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-[#e5e3de] rounded w-3/4" />
                      <div className="h-3 bg-[#e5e3de] rounded w-1/2" />
                    </div>
                  </div>
                ))
              ) : listings.length === 0 ? (
                <div className="text-center py-12">
                  <MapPin className="w-8 h-8 text-[#d5d3ce] mx-auto mb-3" />
                  <p className="text-sm text-[#888]">No listings in this area.</p>
                  <p className="text-xs text-[#888] mt-1">Try increasing the radius or moving the search area.</p>
                </div>
              ) : (
                listings.map(listing => (
                  <a
                    key={listing.listing_id}
                    href={`/listing/${listing.listing_id}`}
                    data-testid={`map-card-${listing.listing_id}`}
                    className="bg-white rounded-xl p-3 border border-black/[0.06] flex gap-3 hover:shadow-md transition-shadow group"
                  >
                    <img
                      src={photoUrl(listing.photos?.[0]) || "https://images.unsplash.com/photo-1666282167632-c613fbeb163c?w=300"}
                      alt=""
                      className="w-28 h-20 rounded-lg object-cover flex-shrink-0 group-hover:scale-[1.02] transition-transform"
                      onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1666282167632-c613fbeb163c?w=300"; }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-black text-[#0d0d0d] text-sm truncate font-['Fraunces']">{listing.title}</p>
                      <p className="text-[#1a6b3c] font-bold text-base">${listing.price?.toLocaleString()}<span className="text-[10px] font-normal text-[#888]">/mo</span></p>
                      <div className="flex items-center gap-1 text-[11px] text-[#888] mt-0.5">
                        <MapPin className="w-3 h-3" /> {listing.city}
                        {listing.distance_miles !== undefined && (
                          <span className="ml-1 text-[#1a6b3c] font-semibold">· {listing.distance_miles}mi</span>
                        )}
                      </div>
                    </div>
                  </a>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
